import React, { useState, useEffect, useMemo, CSSProperties } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { Search, TrendingUp, TrendingDown, Plus, Settings, Play, Pause, BarChart3 } from 'lucide-react';

// --- Types ---
type TitleSize = 'large' | 'medium' | 'small';

interface Stock {
  symbol: string;
  name: string;
  sector: string;
  basePrice: number;
}

interface PricePoint {
  time: string;
  price: number;
  volume: number;
}

type BadgeVariant = 'default' | 'success' | 'danger' | 'warning' | 'info';
type ButtonVariant = 'primary' | 'secondary' | 'success' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

interface Strategy {
  id: string;
  name: string;
  type: string;
  buyThreshold?: string;
  sellThreshold?: string;
  maxInvestment?: string;
  stopLoss?: string;
  takeProfit?: string;
  symbol: string;
  status: 'active' | 'inactive' | string;
  createdAt: string;
}

// Styled Components
const Container: React.FC<React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>> = ({ children, ...props }) => (
  <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', padding: '24px' }} {...props}>
    {children}
  </div>
);

const MainContent: React.FC<React.PropsWithChildren<{}>> = ({ children }) => (
  <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
    {children}
  </div>
);

const Header: React.FC<React.PropsWithChildren<{}>> = ({ children }) => (
  <div style={{ marginBottom: '32px' }}>
    {children}
  </div>
);

const Title: React.FC<React.PropsWithChildren<{ size?: TitleSize; style?: React.CSSProperties }>> = ({ children, size = 'large', style }) => {
  const styles: Record<TitleSize, CSSProperties> = {
    large: { fontSize: '30px', fontWeight: 'bold', color: '#111827', marginBottom: '8px' },
    medium: { fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '16px' },
    small: { fontSize: '16px', fontWeight: '500', color: '#111827', margin: '0' }
  };
  return <h1 style={{ ...styles[size], ...style }}>{children}</h1>;
};

const Subtitle: React.FC<React.PropsWithChildren<{}>> = ({ children }) => (
  <p style={{ color: '#6b7280' }}>{children}</p>
);

const FlexRow: React.FC<React.PropsWithChildren<{ gap?: number; justify?: string; align?: string }>> = ({ children, gap = 16, justify = 'flex-start', align = 'center' }) => (
  <div style={{ 
    display: 'flex', 
    gap: `${gap}px`, 
    justifyContent: justify, 
    alignItems: align,
    marginBottom: '24px'
  }}>
    {children}
  </div>
);

const Grid: React.FC<React.PropsWithChildren<{ columns?: string; gap?: number }>> = ({ children, columns = '12', gap = 24 }) => (
  <div style={{ 
    display: 'grid', 
    gridTemplateColumns: `repeat(${columns}, 1fr)`, 
    gap: `${gap}px` 
  }}>
    {children}
  </div>
);

const GridItem: React.FC<React.PropsWithChildren<{ span?: string; className?: string }>> = ({ children, span = '1', className = '' }) => (
  <div style={{ gridColumn: `span ${span}` }} className={className}>
    {children}
  </div>
);

// Mock data for 50 stocks
const MOCK_STOCKS = [
  { symbol: 'AAPL', name: 'Apple Inc.', sector: 'Technology', basePrice: 175.50 },
  { symbol: 'GOOGL', name: 'Alphabet Inc.', sector: 'Technology', basePrice: 132.85 },
  { symbol: 'MSFT', name: 'Microsoft Corp.', sector: 'Technology', basePrice: 378.85 },
  { symbol: 'AMZN', name: 'Amazon.com Inc.', sector: 'Consumer Discretionary', basePrice: 151.94 },
  { symbol: 'TSLA', name: 'Tesla Inc.', sector: 'Consumer Discretionary', basePrice: 248.50 },
  { symbol: 'META', name: 'Meta Platforms Inc.', sector: 'Technology', basePrice: 325.67 },
  { symbol: 'NVDA', name: 'NVIDIA Corp.', sector: 'Technology', basePrice: 875.28 },
  { symbol: 'JPM', name: 'JPMorgan Chase & Co.', sector: 'Financials', basePrice: 165.32 },
  { symbol: 'JNJ', name: 'Johnson & Johnson', sector: 'Healthcare', basePrice: 159.75 },
  { symbol: 'V', name: 'Visa Inc.', sector: 'Financials', basePrice: 267.89 }
];

// Generate realistic price data with trends
const generatePriceData = (basePrice: number, points = 50): PricePoint[] => {
  const data = [];
  let currentPrice = basePrice;
  const trend = (Math.random() - 0.5) * 0.02;
  
  for (let i = 0; i < points; i++) {
    const volatility = (Math.random() - 0.5) * 0.05;
    const change = trend + volatility;
    currentPrice = currentPrice * (1 + change);
    
    data.push({
      time: new Date(Date.now() - (points - i) * 60000).toLocaleTimeString(),
      price: parseFloat(currentPrice.toFixed(2)),
      volume: Math.floor(Math.random() * 1000000) + 100000
    });
  }
  return data;
};

// Atoms
const SearchInput: React.FC<{ value: string; onChange: (v: string) => void; placeholder?: string }> = ({ value, onChange, placeholder }) => (
  <div style={{ position: 'relative', flex: '1', maxWidth: '384px' }}>
    <Search 
      style={{ 
        position: 'absolute', 
        left: '12px', 
        top: '50%', 
        transform: 'translateY(-50%)', 
        color: '#9ca3af',
        width: '16px',
        height: '16px'
      }} 
    />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%',
        paddingLeft: '40px',
        paddingRight: '16px',
        paddingTop: '8px',
        paddingBottom: '8px',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        fontSize: '14px',
        outline: 'none',
        transition: 'all 0.2s'
      }}
      onFocus={(e) => {
        e.target.style.borderColor = '#3b82f6';
        e.target.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.2)';
      }}
      onBlur={(e) => {
        e.target.style.borderColor = '#d1d5db';
        e.target.style.boxShadow = 'none';
      }}
    />
  </div>
);

const Badge: React.FC<React.PropsWithChildren<{ variant?: BadgeVariant }>> = ({ children, variant = 'default' }) => {
  const variants = {
    default: { backgroundColor: '#f3f4f6', color: '#374151' },
    success: { backgroundColor: '#dcfce7', color: '#166534' },
    danger: { backgroundColor: '#fee2e2', color: '#dc2626' },
    warning: { backgroundColor: '#fef3c7', color: '#d97706' },
    info: { backgroundColor: '#dbeafe', color: '#1d4ed8' }
  };
  
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 10px',
      borderRadius: '9999px',
      fontSize: '12px',
      fontWeight: '500',
      ...variants[variant]
    }}>
      {children}
    </span>
  );
};

const Button: React.FC<React.PropsWithChildren<{ onClick?: () => void; variant?: ButtonVariant; size?: ButtonSize; disabled?: boolean; icon?: React.ComponentType<any> }>> = ({ children, onClick, variant = 'primary', size = 'md', disabled = false, icon: Icon }) => {
  const variants = {
    primary: { backgroundColor: '#2563eb', color: 'white', hoverBg: '#1d4ed8' },
    secondary: { backgroundColor: '#e5e7eb', color: '#374151', hoverBg: '#d1d5db' },
    success: { backgroundColor: '#059669', color: 'white', hoverBg: '#047857' },
    danger: { backgroundColor: '#dc2626', color: 'white', hoverBg: '#b91c1c' }
  };
  
  const sizes = {
    sm: { padding: '6px 12px', fontSize: '14px' },
    md: { padding: '8px 16px', fontSize: '14px' },
    lg: { padding: '12px 24px', fontSize: '16px' }
  };
  
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        fontWeight: '500',
        borderRadius: '8px',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.2s',
  backgroundColor: isHovered && !disabled ? variants[variant as ButtonVariant].hoverBg : variants[variant as ButtonVariant].backgroundColor,
  color: variants[variant as ButtonVariant].color,
  ...sizes[size as ButtonSize]
      }}
    >
      {Icon && <Icon style={{ width: '16px', height: '16px' }} />}
      {children}
    </button>
  );
};

const Card: React.FC<React.PropsWithChildren<{ onClick?: () => void; selected?: boolean }>> = ({ children, onClick, selected = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        border: selected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
        boxShadow: isHovered ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s'
      }}
    >
      {children}
    </div>
  );
};

// Molecules
const StockCard: React.FC<{ stock: Stock; onClick: (s: Stock) => void; isSelected?: boolean }> = ({ stock, onClick, isSelected }) => {
  const priceChange = ((Math.random() - 0.5) * 10).toFixed(2);
  const percentChange = ((parseFloat(priceChange) / stock.basePrice) * 100).toFixed(2);
  const isPositive = parseFloat(priceChange) >= 0;
  
  return (
    <Card onClick={() => onClick(stock)} selected={isSelected}>
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <div>
            <h3 style={{ fontWeight: '600', color: '#111827', fontSize: '16px', margin: '0' }}>
              {stock.symbol}
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0 0' }}>
              {stock.name}
            </p>
          </div>
          <Badge variant="info">{stock.sector}</Badge>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
            <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', margin: '0' }}>
              ${stock.basePrice.toFixed(2)}
            </p>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              fontSize: '14px', 
              color: isPositive ? '#059669' : '#dc2626',
              marginTop: '4px'
            }}>
              {isPositive ? 
                <TrendingUp style={{ width: '16px', height: '16px', marginRight: '4px' }} /> : 
                <TrendingDown style={{ width: '16px', height: '16px', marginRight: '4px' }} />
              }
              ${Math.abs(parseFloat(priceChange))} ({Math.abs(parseFloat(percentChange))}%)
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

const StockChart: React.FC<{ data: PricePoint[]; title?: string }> = ({ data, title }) => (
  <Card>
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <Title size="medium">{title}</Title>
          <div style={{ display: 'flex', gap: '8px' }}>
          <Button variant="secondary" size="sm" icon={Settings} onClick={() => {}}>Settings</Button>
          <Button variant="secondary" size="sm" icon={BarChart3} onClick={() => {}}>Indicators</Button>
        </div>
      </div>
      
      <div style={{ height: '320px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <defs>
              <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" style={{ opacity: 0.3 }} />
            <XAxis dataKey="time" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'white', 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px',
                fontSize: '12px'
              }} 
            />
            <Area 
              type="monotone" 
              dataKey="price" 
              stroke="#3B82F6" 
              strokeWidth={2}
              fillOpacity={1} 
              fill="url(#colorPrice)" 
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  </Card>
);

const StrategyForm: React.FC<{ selectedStock: Stock; onSave: (s: Strategy) => void; onCancel: () => void }> = ({ selectedStock, onSave, onCancel }) => {
  const [strategy, setStrategy] = useState({
    name: '',
    type: 'buy_low_sell_high',
    buyThreshold: '',
    sellThreshold: '',
    maxInvestment: '',
    stopLoss: '',
    takeProfit: ''
  });
  
  const handleSubmit = () => {
    if (!strategy.name) return;
    
    onSave({
      ...strategy,
      symbol: selectedStock.symbol,
      id: Date.now().toString(),
      status: 'inactive',
      createdAt: new Date().toISOString()
    });
    setStrategy({
      name: '',
      type: 'buy_low_sell_high',
      buyThreshold: '',
      sellThreshold: '',
      maxInvestment: '',
      stopLoss: '',
      takeProfit: ''
    });
  };
  
  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none'
  };
  
  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '4px'
  };
  
  return (
    <Card>
      <div style={{ padding: '24px' }}>
        <Title size="medium">Create Strategy for {selectedStock.symbol}</Title>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={labelStyle}>Strategy Name</label>
            <input
              type="text"
              value={strategy.name}
              onChange={(e) => setStrategy(prev => ({ ...prev, name: e.target.value }))}
              style={inputStyle}
              placeholder="My Trading Strategy"
            />
          </div>
          
          <div>
            <label style={labelStyle}>Strategy Type</label>
            <select
              value={strategy.type}
              onChange={(e) => setStrategy(prev => ({ ...prev, type: e.target.value }))}
              style={inputStyle}
            >
              <option value="buy_low_sell_high">Buy Low, Sell High</option>
              <option value="momentum">Momentum Trading</option>
              <option value="mean_reversion">Mean Reversion</option>
              <option value="breakout">Breakout Strategy</option>
            </select>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Buy Threshold ($)</label>
              <input
                type="number"
                step="0.01"
                value={strategy.buyThreshold}
                onChange={(e) => setStrategy(prev => ({ ...prev, buyThreshold: e.target.value }))}
                style={inputStyle}
                placeholder="0.00"
              />
            </div>
            
            <div>
              <label style={labelStyle}>Sell Threshold ($)</label>
              <input
                type="number"
                step="0.01"
                value={strategy.sellThreshold}
                onChange={(e) => setStrategy(prev => ({ ...prev, sellThreshold: e.target.value }))}
                style={inputStyle}
                placeholder="0.00"
              />
            </div>
          </div>
          
          <div>
            <label style={labelStyle}>Max Investment ($)</label>
            <input
              type="number"
              step="0.01"
              value={strategy.maxInvestment}
              onChange={(e) => setStrategy(prev => ({ ...prev, maxInvestment: e.target.value }))}
              style={inputStyle}
              placeholder="1000.00"
            />
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Stop Loss (%)</label>
              <input
                type="number"
                step="0.1"
                value={strategy.stopLoss}
                onChange={(e) => setStrategy(prev => ({ ...prev, stopLoss: e.target.value }))}
                style={inputStyle}
                placeholder="5.0"
              />
            </div>
            
            <div>
              <label style={labelStyle}>Take Profit (%)</label>
              <input
                type="number"
                step="0.1"
                value={strategy.takeProfit}
                onChange={(e) => setStrategy(prev => ({ ...prev, takeProfit: e.target.value }))}
                style={inputStyle}
                placeholder="10.0"
              />
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', paddingTop: '16px' }}>
            <Button onClick={handleSubmit} variant="success" icon={Plus}>
              Create Strategy
            </Button>
            <Button variant="secondary" onClick={onCancel} icon={() => null}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

const StrategiesList: React.FC<{ strategies: Strategy[]; onToggleStrategy: (id: string) => void }> = ({ strategies, onToggleStrategy }) => (
  <Card>
    <div style={{ padding: '24px' }}>
      <Title size="medium">Active Strategies</Title>
      
    {strategies.length === 0 ? (
        <p style={{ color: '#6b7280', textAlign: 'center', padding: '32px 0' }}>
          No strategies created yet
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {strategies.map((strategy) => (
            <div 
              key={strategy.id} 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                padding: '16px', 
                backgroundColor: '#f9fafb', 
                borderRadius: '8px' 
              }}
            >
              <div>
                <h4 style={{ fontWeight: '500', color: '#111827', margin: '0' }}>
                  {strategy.name}
                </h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '4px' }}>
                  <span style={{ fontSize: '14px', color: '#6b7280' }}>{strategy.symbol}</span>
                  <Badge variant={strategy.status === 'active' ? 'success' : 'default'}>
                    {strategy.status}
                  </Badge>
                  <span style={{ fontSize: '14px', color: '#6b7280' }}>{strategy.type}</span>
                </div>
              </div>
              
              <Button
                variant={strategy.status === 'active' ? 'danger' : 'success'}
                size="sm"
                icon={strategy.status === 'active' ? Pause : Play}
                onClick={() => onToggleStrategy(strategy.id)}
              >
                {strategy.status === 'active' ? 'Pause' : 'Activate'}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  </Card>
);

// Main Component
const LiveReports: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedStock, setSelectedStock] = useState<Stock>(MOCK_STOCKS[0]);
  const [showStrategyForm, setShowStrategyForm] = useState<boolean>(false);
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [chartData, setChartData] = useState<PricePoint[]>([]);
  
  // Filter stocks based on search
  const filteredStocks = useMemo(() => {
    return MOCK_STOCKS.filter(stock =>
      stock.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
      stock.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      stock.sector.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm]);
  
  // Generate chart data for selected stock
  useEffect(() => {
    if (selectedStock) {
      const data = generatePriceData(selectedStock.basePrice);
      setChartData(data);
    }
  }, [selectedStock]);
  
  // Simulate real-time data updates
  useEffect(() => {
    const interval = setInterval(() => {
      if (selectedStock && chartData.length > 0) {
        setChartData(prevData => {
          const newData = [...prevData];
          const lastPrice = newData[newData.length - 1].price;
          const change = (Math.random() - 0.5) * 0.02;
          const newPrice = parseFloat((lastPrice * (1 + change)).toFixed(2));
          
          newData.push({
            time: new Date().toLocaleTimeString(),
            price: newPrice,
            volume: Math.floor(Math.random() * 1000000) + 100000
          });
          
          return newData.slice(-50);
        });
      }
    }, 3000);
    
    return () => clearInterval(interval);
  }, [selectedStock, chartData.length]);
  
  const handleSaveStrategy = (strategy: Strategy) => {
    setStrategies(prev => [...prev, strategy]);
    setShowStrategyForm(false);
  };
  
  const handleToggleStrategy = (strategyId: string) => {
    setStrategies(prev =>
      prev.map(strategy =>
        strategy.id === strategyId
          ? { ...strategy, status: strategy.status === 'active' ? 'inactive' : 'active' }
          : strategy
      )
    );
  };
  
  return (
    <Container>
      <MainContent>
        <Header>
          <Title size="large">Stock Dashboard</Title>
          <Subtitle>Real-time stock data and strategy management</Subtitle>
        </Header>
        
        <FlexRow>
          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search stocks by symbol, name, or sector..."
          />
          <Button
            variant="success"
            icon={Plus}
            onClick={() => setShowStrategyForm(true)}
            disabled={!selectedStock}
          >
            Create Strategy
          </Button>
        </FlexRow>
        
        <Grid columns="12" gap={24}>
          <GridItem span="3">
            <Card>
              <div style={{ padding: '16px', maxHeight: '400px', overflowY: 'auto' }}>
                <Title size="small" style={{ marginBottom: '16px' }}>
                  Stocks ({filteredStocks.length})
                </Title>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {filteredStocks.map(stock => (
                    <StockCard
                      key={stock.symbol}
                      stock={stock}
                      onClick={setSelectedStock}
                      isSelected={selectedStock?.symbol === stock.symbol}
                    />
                  ))}
                </div>
              </div>
            </Card>
          </GridItem>
          
          <GridItem span="9">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <StockChart
                data={chartData}
                title={`${selectedStock.symbol} - ${selectedStock.name}`}
              />
              
              {showStrategyForm ? (
                <StrategyForm
                  selectedStock={selectedStock}
                  onSave={handleSaveStrategy}
                  onCancel={() => setShowStrategyForm(false)}
                />
              ) : (
                <StrategiesList
                  strategies={strategies}
                  onToggleStrategy={handleToggleStrategy}
                />
              )}
            </div>
          </GridItem>
        </Grid>
      </MainContent>
    </Container>
  );
};

export default LiveReports;
export { LiveReports };